import { supabase } from './supabase'

// Single-flight forced session refresh shared across all three client
// auth-error transports: the `api` axios instance (lib/api.js), the default
// axios singleton (context/AuthContext.jsx), and the native-fetch path
// (lib/authErrorHandlers.js fetchWithAuthRetry). N concurrent 401s collapse to
// ONE supabase.auth.refreshSession() call — every waiter shares the same
// in-flight promise and result.
//
// Resolves to the new access-token string, or null on any failure (never
// throws — callers treat null as "refresh failed, fall through to logout").
// `inFlight` is cleared in finally so a later genuine staleness can refresh
// again; while a refresh is in flight, concurrent callers reuse the promise.
let inFlight = null

export function refreshSessionOnce() {
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) return null
      return data?.session?.access_token ?? null
    } catch {
      return null
    }
  })().finally(() => { inFlight = null })
  return inFlight
}

// Shared refresh-and-retry branch for both axios response-error interceptors.
// On a 401 AUTH_INVALID (and only that), refresh once via the single-flight
// helper and replay the original request once with the freshly-refreshed token.
//
// Returns the replayed request's promise when a retry was performed, or null
// when the request is NOT eligible OR the refresh failed — in both null cases
// the caller runs its existing dispatchAuthError + reject (logout) path.
//
// Single-retry guarantee: `config._authRetried` is set before the replay, so a
// replayed request that 401s AGAIN is not eligible (returns null) and falls
// straight to logout — it can never loop. Trigger is scoped to AUTH_INVALID, so
// NO_AUTH, SESSION_INVALIDATED, 403 state gates, and 503 AUTH_TRANSIENT are
// never refresh-retried. skipAuthDispatch (fire-and-forget bootstrap pings) is
// honored here too — never refreshed/retried.
//
// Header overwrite is load-bearing for the default singleton, which has NO
// request interceptor: the replayed `config` still carries the caller's stale
// Authorization header, so it MUST be overwritten with the fresh token before
// replaying. (The `api` instance also re-attaches via its request interceptor;
// the overwrite there is belt-and-suspenders.)
export async function tryRefreshAndReplay(err, instance) {
  const status = err?.response?.status
  const code = err?.response?.data?.code
  const config = err?.config
  if (status !== 401 || code !== 'AUTH_INVALID') return null
  if (!config || config._authRetried || config.skipAuthDispatch) return null
  config._authRetried = true
  const token = await refreshSessionOnce()
  if (!token) return null
  config.headers = { ...(config.headers ?? {}), Authorization: `Bearer ${token}` }
  return instance(config)
}
